import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Spin, Typography } from 'antd';
import { apiConfig } from '../authConfig';
import { apiErrorFromResponse, userMessageForError } from '../utils/apiError';
import { formatDdMmYyyy } from '../utils/formatDdMmYyyy';
import DataCard from './page/DataCard';
import { BarList, Radar, isNum } from './charts';

const { Title, Text, Paragraph } = Typography;

const FALLBACK_DISCLAIMER = 'Indicative only: derived from the ISO 27002 cross-framework mapping, not an assessment against each regulation.';

/** Frameworks with no loaded controls are not charted (a 0% there would be a claim about nothing). */
export function chartableFrameworks(frameworks) {
  return (Array.isArray(frameworks) ? frameworks : []).filter((f) => f && isNum(f.totalControls) && f.totalControls > 0 && isNum(f.coveragePct) && isNum(f.implementedPct));
}

/** "Implementing your ISO 27002 controls covers 40% of NIST CSF, 12% of SOC 2." */
export function coverageHeadline(frameworks) {
  if (!frameworks.length) return null;
  return `Implementing your ISO 27002 controls covers ${frameworks.map((f) => `${f.implementedPct}% of ${f.label}`).join(', ')}.`;
}

/**
 * Cross-regulation coverage for one project: GET /api/v1/projects/:id/cross-framework-coverage.
 * Derived from the ISO 27002 mapping (Control Taxonomy), so it is indicative; the API's own disclaimer is shown verbatim.
 */
const CrossRegulationCoverage = ({ projectId, getApiToken, isAdmin, onEditMapping }) => {
  const [state, setState] = useState({ status: 'loading', data: null, message: null, httpStatus: null });

  const load = useCallback(async () => {
    setState({ status: 'loading', data: null, message: null, httpStatus: null });
    try {
      const token = await getApiToken();
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/cross-framework-coverage`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) throw apiErrorFromResponse(res, json);
      if (!json.data || !Array.isArray(json.data.frameworks)) {
        setState({ status: 'error', data: null, message: 'The coverage response was not in the expected format.', httpStatus: null });
        return;
      }
      setState({ status: 'ready', data: json.data, message: null, httpStatus: null });
    } catch (err) {
      setState({ status: 'error', data: null, message: userMessageForError(err, 'Could not load cross-regulation coverage.'), httpStatus: err?.status ?? null });
    }
    // getApiToken is a new function each render in the hook; keyed on the project only.
  }, [projectId]);

  useEffect(() => { if (projectId) load(); }, [projectId]);

  const { status, data, message, httpStatus } = state;
  const frameworks = status === 'ready' ? chartableFrameworks(data.frameworks) : [];
  const basis = data?.basis;

  let body;
  if (status === 'loading') {
    body = <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>;
  } else if (status === 'error') {
    body = (
      <Alert
        type="warning"
        showIcon
        data-testid="coverage-error"
        message={httpStatus === 404 ? 'Cross-regulation coverage is not available on this server yet' : 'Cross-regulation coverage could not be loaded'}
        description={message}
        action={<Button size="small" onClick={load}>Retry</Button>}
      />
    );
  } else if (!frameworks.length) {
    body = <Empty data-testid="coverage-empty" description="No regulation controls are loaded for comparison yet. Load them in Policy Hub and map them to ISO 27002 in Control Taxonomy." />;
  } else {
    body = (
      <>
        <Paragraph data-testid="coverage-headline" style={{ fontSize: 16 }}>{coverageHeadline(frameworks)}</Paragraph>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
          <div style={{ flex: '2 1 360px', minWidth: 0 }}>
            <BarList
              max={100}
              label="Coverage by regulation"
              baseName="Covered by in-scope ISO controls"
              layerName="Covered by implemented ISO controls"
              testId="coverage-bars"
              rows={frameworks.map((f) => ({
                key: f.key,
                label: f.label,
                value: f.coveragePct,
                layerValue: f.implementedPct,
                valueText: `${f.implementedPct}% implemented, ${f.coveragePct}% in scope`,
                title: `${f.mappedControls} of ${f.totalControls} ${f.label} controls are mapped to ISO 27002`,
              }))}
            />
          </div>
          {frameworks.length >= 3 && (
            <div style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
              <Radar label="In-scope coverage by regulation" testId="coverage-radar" axes={frameworks.map((f) => ({ key: f.key, label: f.label, value: f.coveragePct }))} />
            </div>
          )}
        </div>
        {basis && (
          <Text type="secondary" data-testid="coverage-basis" style={{ display: 'block', marginTop: 12 }}>
            Based on {basis.implementedIsoControls} implemented of {basis.inScopeIsoControls} in-scope ISO 27002 controls{basis.asOf ? `. As of ${formatDdMmYyyy(basis.asOf)}` : ''}.
          </Text>
        )}
      </>
    );
  }

  return (
    <DataCard size="small" data-testid="cross-regulation-coverage">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <Title level={4} style={{ marginTop: 0 }}>Cross-regulation coverage</Title>
        {isAdmin && onEditMapping && <Button type="link" data-testid="coverage-edit-mapping" onClick={onEditMapping}>Edit mapping</Button>}
      </div>
      {body}
      {status === 'ready' && (
        <Text type="secondary" data-testid="coverage-disclaimer" style={{ display: 'block', marginTop: 8 }}>{data.disclaimer || FALLBACK_DISCLAIMER}</Text>
      )}
    </DataCard>
  );
};

export default CrossRegulationCoverage;
